import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor, HttpErrorResponse, HttpStatusCode, HttpContextToken } from '@angular/common/http';
import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { catchError, switchMap, throwError, Observable, from, finalize } from 'rxjs';
import { ServerErrorComponent } from 'src/app/layout/_global-errors/server-error/server-error.component';
import { AppConfigurationService } from '../services/app-configuration.service';
import { AuthService } from '../services/auth.service';
import { LoggingService } from '../services/logging.service';
import { ModalService } from '../services/modal.service';
import { NavigationService } from '../services/navigation.service';
import { PopupService } from '../services/popup.service';
import { LoadingService } from '../services/loading.service';
import { loadingDataRequest } from '../services/api/base-api.service';
import { PageLoadingContext } from './page-loading-context.type';

export const throwErrorOnUnauthorizedToken = new HttpContextToken<boolean>(() => false);
export const logoutOnHandledErrorToken = new HttpContextToken<boolean>(() => false);

const ERROR_MESSAGES = {
  OFFLINE: 'Please check your internet connection.',
  NEW_VERSION: 'Please refresh your browser to continue using Pointee and ensure everything works correctly.',
  SERVER_ERROR_TITLE: 'Server Error',
  OFFLINE_TITLE: 'Server not responding',
  NEW_VERSION_TITLE: 'New version available',
  TRY_AGAIN_ACTION: 'Try again',
  REFRESH_ACTION: 'Refresh',
};

const SERVER_ERROR_CODES = [
  HttpStatusCode.InternalServerError,
  HttpStatusCode.NotImplemented,
  HttpStatusCode.BadGateway,
  HttpStatusCode.ServiceUnavailable,
  HttpStatusCode.HttpVersionNotSupported,
];

type ErrorHandlerResult = Observable<HttpEvent<unknown>>;

/**
 * Intercepts HTTP requests to:
 * 1. Add common headers (Content-Type, Accept, Accept-Language, X-Client-Version)
 * 2. Handle authentication errors and token refresh
 * 3. Track page loading time
 * 4. Show/hide loading indicators
 * 5. Handle common error scenarios (offline, server errors, etc.)
 */
@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private pageLoadingContext: PageLoadingContext;

  constructor(
    private authService: AuthService,
    private loggingService: LoggingService,
    private popupService: PopupService,
    private navigationService: NavigationService,
    private modalService: ModalService,
    private appConfiguration: AppConfigurationService,
    private loadingService: LoadingService,
    @Inject(LOCALE_ID) private localeId: string,
  ) {
    this.pageLoadingContext = new PageLoadingContext(this.loggingService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.handle(request, next, true);
  }

  private isLoadingDataRequest(request: HttpRequest<unknown>, firstTry = true): boolean {
    // Exclude chat API requests from showing loading indicator
    if (request.url.includes('/api/chat')) {
      return false;
    }
    return (firstTry && request.method === 'GET') || request.context.get(loadingDataRequest);
  }

  private handle(request: HttpRequest<unknown>, next: HttpHandler, firstTry: boolean): Observable<HttpEvent<unknown>> {
    if (this.isLoadingDataRequest(request)) {
      this.loadingService.showLoading();
    }
    const updatedRequest = this.updateRequest(request);

    // Track request start time for page loading metrics
    if (this.isLoadingDataRequest(request, firstTry)) {
      this.pageLoadingContext.trackRequestStart(
        request.url,
        this.navigationService.currentModule,
        this.navigationService.currentPage,
        this.navigationService.currentSubPage);
    }

    return next.handle(updatedRequest).pipe(
      catchError((error: unknown) => this.handleError(error, updatedRequest, next, firstTry)),
      finalize(() => {
        // Track request end time for page loading metrics
        if (firstTry && request.method === 'GET') {
          this.pageLoadingContext.trackRequestEnd(request.url);
        }
        this.finishRequest();
      }),
    );
  }

  /**
   * Handles cleanup after a request completes
   */
  private finishRequest(): void {
    this.loadingService.hideLoading();
    this.pageLoadingContext.end();
  }

  private handleError(error: unknown, request: HttpRequest<unknown>, next: HttpHandler, firstTry: boolean): ErrorHandlerResult {
    // Only handle HttpErrorResponse or ErrorEvent types
    if (!(error instanceof HttpErrorResponse) && !(error instanceof ErrorEvent)) {
      return throwError(() => error);
    }

    // Handle client-side errors (network issues)
    if (this.isClientSideError(error)) {
      return this.handleClientError(error as Error);
    }

    // Handle specific HTTP status codes
    if (error instanceof HttpErrorResponse) {
      return this.handleHttpError(error, request, next, firstTry);
    }

    return throwError(() => error);
  }

  private isClientSideError(error: unknown): boolean {
    if (error instanceof ErrorEvent) {
      return true;
    }

    if (!navigator.onLine) {
      return true;
    }

    // Check for network timeouts or connection refused
    if (error instanceof HttpErrorResponse) {
      const innerError = error.error as { status?: number };
      return innerError?.status === HttpStatusCode.GatewayTimeout || innerError?.status === 0;
    }

    return false;
  }

  private handleClientError(error: Error): ErrorHandlerResult {
    const errorMessage = error instanceof ErrorEvent ? error.message : 'Network error';
    const clientError = `Error: ${errorMessage}`;

    this.loggingService.logError(clientError, { error });

    void this.popupService.showInfo(
      ERROR_MESSAGES.OFFLINE,
      ERROR_MESSAGES.OFFLINE_TITLE,
      ERROR_MESSAGES.TRY_AGAIN_ACTION,
    ).then(() => window.location.reload());

    return throwError(() => error);
  }

  private handleHttpError(error: HttpErrorResponse, request: HttpRequest<unknown>, next: HttpHandler, firstTry: boolean): ErrorHandlerResult {
    switch (error.status) {
      case HttpStatusCode.Unauthorized:
        return this.handleUnauthorizedError(error, request, next, firstTry);

      case HttpStatusCode.ImATeapot: // API version is not supported
        return this.handleVersionError();

      default:
        if (SERVER_ERROR_CODES.includes(error.status)) {
          return this.handleServerError(error, request);
        }
        return throwError(() => error);
    }
  }

  private handleUnauthorizedError(error: HttpErrorResponse, request: HttpRequest<unknown>, next: HttpHandler, firstTry: boolean): ErrorHandlerResult {
    if (request.context.get(throwErrorOnUnauthorizedToken)) {
      return throwError(() => error);
    }

    if (request.context.get(logoutOnHandledErrorToken) || !firstTry) {
      return this.navigateToLogin(error, `API call to "${request.url}" returned error 401`, false);
    }

    return from(this.authService.refreshToken()).pipe(
      switchMap(() => this.handle(request, next, false)),
      catchError(refreshError => {
        this.loggingService.logError('Failed to refresh tokens in HTTP interceptor', { error: refreshError });
        return this.navigateToLogin(error, 'Failed to refresh tokens in HTTP interceptor', false);
      }),
    );
  }

  private handleVersionError(): ErrorHandlerResult {
    void this.popupService.showInfo(
      ERROR_MESSAGES.NEW_VERSION,
      ERROR_MESSAGES.NEW_VERSION_TITLE,
      ERROR_MESSAGES.REFRESH_ACTION,
      false,
    ).then(() => window.location.reload());
    return new Observable();
  }

  private handleServerError(error: HttpErrorResponse, request: HttpRequest<unknown>): ErrorHandlerResult {
    if (request.context.get(logoutOnHandledErrorToken)) {
      return this.navigateToLogin(error, `API call to "${request.url}" returned error ${error.status}`);
    }

    this.pageLoadingContext.clear();

    const errorMsg = `Error Code: ${error.status}\nServer Error when obtaining ${error.url}\nMessage: ${error.message}`;
    this.loggingService.logError(errorMsg, { error });
    this.modalService.openSingle(ServerErrorComponent, d => d);

    return throwError(() => new HttpErrorResponse(error));
  }

  private navigateToLogin(error: HttpErrorResponse, reason: string, showReason = true, useRedirectUrl = true): ErrorHandlerResult {
    this.pageLoadingContext.clear();
    return from(
      this.navigationService.navigateToLogin(reason, showReason, useRedirectUrl),
    ).pipe(switchMap(() => throwError(() => error)));
  }

  private updateRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = request.headers;

    if (this.appConfiguration.clientVersion != null) {
      headers = headers.set('X-Client-Version', this.appConfiguration.clientVersion);
    }

    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    // For uploading files as formData, we need to remove Content-Type
    // to let the browser set it with the correct boundary
    if (headers.get('Content-Type') === 'multipart/form-data') {
      headers = headers.delete('Content-Type');
    }

    headers = headers.set('Accept', 'application/json').set('Accept-Language', this.localeId);
    return request.clone({ headers });
  }
}
